import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { msalConfig } from '@/auth/authConfig';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { env } from '@/env';
import { initializeAppInsights } from '@/services/appInsights';
import { logger } from '@/services/logger';
import App from './App';
import './index.css';

/**
 * Initialize Application Insights before the first render, so errors thrown while rendering are
 * reported, and exactly once (a mount effect runs twice under StrictMode)
 */
const appInsights = initializeAppInsights(env.VITE_APPINSIGHTS_CONNECTION_STRING);
logger.initialize(appInsights);

if (appInsights) {
  logger.info('Application started', {
    environment: import.meta.env.MODE,
    version: import.meta.env.VITE_APP_VERSION || 'unknown',
  });
}

/**
 * Initialize MSAL instance
 */
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize React Query client
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Render the application with providers
 */
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
